import { CloseButton, FocusTrap, ScrollArea, UnstyledButton } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { useState } from "react";

import type { Catalog } from "../../../api/generated/model";
import { ActionCard } from "./ActionCard";
import classes from "./actions.module.css";
import { ACTION_TOOLS, type ActionTool, type ToolKind } from "./index";

/** Live's tabs: every card, the Functions, the Tools. */
const TABS: { label: string; kind: ToolKind | null }[] = [
  { label: "All", kind: null },
  { label: "Functions", kind: "function" },
  { label: "Tools", kind: "tool" },
];

export interface ActionsPanelProps {
  catalog: Catalog;
  chosen: ActionTool | null;
  onChoose: (action: ActionTool) => void;
  onClose: () => void;
}

/**
 * Live's Actions panel (#227, captures 18 to 20): over the composer, the tabs All, Functions and
 * Tools, a close x, and a three-column grid of the 13 cards, each with its count of the
 * catalog's models that take it. A card no model takes is disabled, as live's is; choosing one
 * closes the panel. Ours also closes on Escape, and keeps the focus inside while open.
 */
export function ActionsPanel({ catalog, chosen, onChoose, onClose }: ActionsPanelProps) {
  const [kind, setKind] = useState<ToolKind | null>(null);
  const shown = ACTION_TOOLS.filter((action) => kind === null || action.kind === kind);
  useHotkeys([["Escape", onClose]]);
  return (
    <FocusTrap active>
      <dialog open aria-label="Actions" className={classes.panel}>
        <div className={classes.header}>
          <div className={classes.tabs}>
            {TABS.map((tab) => (
              <UnstyledButton
                key={tab.label}
                className={classes.tab}
                data-active={tab.kind === kind || undefined}
                aria-pressed={tab.kind === kind}
                onClick={() => setKind(tab.kind)}
              >
                {tab.label}
              </UnstyledButton>
            ))}
          </div>
          <CloseButton aria-label="Close the actions" onClick={onClose} />
        </div>
        <ScrollArea.Autosize type="auto" scrollbars="y" scrollbarSize={4} offsetScrollbars>
          <div className={classes.grid}>
            {shown.map((action) => (
              <ActionCard
                key={action.id}
                action={action}
                count={catalog.video_models.filter(action.takes).length}
                active={action.id === chosen?.id}
                onChoose={() => onChoose(action)}
              />
            ))}
          </div>
        </ScrollArea.Autosize>
      </dialog>
    </FocusTrap>
  );
}
